import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  validateShirtName,
  SHIRT_NAME_MAX,
  type ShirtPosition,
  type ShirtSize,
} from '@toca/core';
import { fetchShirtSizes } from '../lib/api';
import { ShirtPreview } from './ShirtPreview';
import { downloadPng, svgToString } from './exportPng';

const CORES = [
  { nome: 'Dourado', valor: '#FFD700' },
  { nome: 'Branco', valor: '#FFFFFF' },
  { nome: 'Laranja', valor: '#FF8C00' },
  { nome: 'Preto', valor: '#111111' },
];

export interface ComprarPayload {
  nomeNaCamisa: string;
  tamanho: string;
  posicao: ShirtPosition;
  cor: string;
  svgSnapshot: string;
}

export function ShirtCustomizer({ onComprar }: { onComprar?: (p: ComprarPayload) => void }) {
  const [nome, setNome] = useState('');
  const [tamanho, setTamanho] = useState('');
  const [posicao, setPosicao] = useState<ShirtPosition>('BACK');
  const [cor, setCor] = useState(CORES[0]!.valor);
  const [sizes, setSizes] = useState<ShirtSize[]>([]);
  const [erroCarregar, setErroCarregar] = useState('');
  const svgRef = useRef<SVGSVGElement>(null);

  // debounce de 120 ms para o preview (exigência do briefing)
  const [nomeDebounced, setNomeDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setNomeDebounced(nome), 120);
    return () => clearTimeout(t);
  }, [nome]);
  const check = useMemo(() => validateShirtName(nome || ' '), [nome]);
  const nomeValido = nome.trim().length > 0 && check.ok;

  useEffect(() => {
    fetchShirtSizes()
      .then((s) => {
        setSizes(s);
        setTamanho((atual) => atual || s[0]?.codigo || '');
      })
      .catch((e: Error) => setErroCarregar(e.message));
  }, []);

  // exposto para validação automatizada (Playwright/puppeteer)
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__exportPng = async () => {
      if (!svgRef.current) return null;
      const { exportSvgToPngBlob } = await import('./exportPng');
      const blob = await exportSvgToPngBlob(svgRef.current);
      return { type: blob.type, size: blob.size };
    };
    (window as unknown as Record<string, unknown>).__svgString = () =>
      svgRef.current ? svgToString(svgRef.current) : '';
  });

  const tamanhoSel = sizes.find((s) => s.codigo === tamanho);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <header className="mb-8 text-center">
        <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-dourado">Toca do Jacaré</p>
        <h1 className="font-display text-3xl font-black sm:text-4xl">Personalize sua Camisa UV</h1>
        <p className="mt-2 text-sm text-amber-100/70">Digite seu nome e veja na hora como fica na camisa de pesca.</p>
      </header>

      <div className="grid gap-8 md:grid-cols-2">
        {/* PREVIEW */}
        <section aria-label="Prévia da camisa" className="order-1 md:order-none">
          <div className="rounded-2xl border border-dourado/20 bg-black/30 p-3 shadow-2xl">
            <ShirtPreview ref={svgRef} name={nomeDebounced} position={posicao} color={cor} />
          </div>
          {/* anuncia a atualização para leitores de tela */}
          <p aria-live="polite" className="sr-only">
            {nomeDebounced ? `Prévia atualizada: ${nomeDebounced}` : 'Prévia sem nome'}
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => svgRef.current && downloadPng(svgRef.current)}
              disabled={!nomeValido}
              className="flex-1 rounded-full border border-dourado/40 px-5 py-3 font-bold text-dourado transition enabled:hover:bg-dourado/10 disabled:opacity-50"
            >
              Baixar prévia (PNG)
            </button>
            {onComprar && (
              <button
                type="button"
                onClick={() => {
                  if (!nomeValido || !tamanho) return;
                  // força o SVG a refletir o nome ATUAL antes de capturar o snapshot
                  flushSync(() => setNomeDebounced(nome));
                  if (svgRef.current) {
                    onComprar({ nomeNaCamisa: nome, tamanho, posicao, cor, svgSnapshot: svgToString(svgRef.current) });
                  }
                }}
                disabled={!nomeValido || !tamanho}
                className="flex-[1.4] rounded-full bg-gradient-to-r from-dourado to-laranja px-5 py-3 font-extrabold text-[#241202] transition enabled:hover:brightness-105 disabled:opacity-50"
              >
                Comprar esta camisa →
              </button>
            )}
          </div>
        </section>

        {/* FORMULÁRIO */}
        <section aria-label="Opções" className="space-y-5">
          <div>
            <label htmlFor="nome" className="mb-1 block text-xs font-bold uppercase tracking-wider text-amber-100/70">
              Nome na camisa
            </label>
            <input
              id="nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={SHIRT_NAME_MAX}
              placeholder="Ex.: IGOR"
              aria-invalid={!check.ok && nome.length > 0}
              aria-describedby="nome-ajuda"
              className="w-full rounded-xl border border-dourado/25 bg-black/40 px-4 py-3 text-lg outline-none focus:border-laranja"
            />
            <p id="nome-ajuda" className="mt-1 flex justify-between text-xs">
              <span className={!check.ok && nome.length > 0 ? 'text-red-400' : 'text-amber-100/50'}>
                {!check.ok && nome.length > 0 ? check.motivo : 'Letras, números, espaço, hífen e apóstrofo.'}
              </span>
              <span className="tabular-nums text-amber-100/50">{nome.length}/{SHIRT_NAME_MAX}</span>
            </p>
          </div>

          <div>
            <label htmlFor="tamanho" className="mb-1 block text-xs font-bold uppercase tracking-wider text-amber-100/70">
              Tamanho
            </label>
            {erroCarregar ? (
              <p className="text-sm text-red-400">{erroCarregar}</p>
            ) : (
              <select
                id="tamanho"
                value={tamanho}
                onChange={(e) => setTamanho(e.target.value)}
                className="w-full rounded-xl border border-dourado/25 bg-black/40 px-4 py-3 outline-none focus:border-laranja"
              >
                {sizes.map((s) => (
                  <option key={s.codigo} value={s.codigo}>
                    {s.codigo} — {s.alturaCm} × {s.larguraCm} cm
                  </option>
                ))}
              </select>
            )}
            {tamanhoSel && (
              <p className="mt-1 text-xs text-amber-100/50">
                Altura {tamanhoSel.alturaCm} cm · Largura {tamanhoSel.larguraCm} cm
              </p>
            )}
          </div>

          <fieldset>
            <legend className="mb-1 block text-xs font-bold uppercase tracking-wider text-amber-100/70">Posição</legend>
            <div className="flex gap-2">
              {(['BACK', 'CHEST'] as ShirtPosition[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={posicao === p}
                  onClick={() => setPosicao(p)}
                  className={`flex-1 rounded-full px-4 py-2 text-sm font-bold transition ${
                    posicao === p ? 'bg-dourado text-[#241202]' : 'border border-dourado/25 text-amber-100/80'
                  }`}
                >
                  {p === 'BACK' ? 'Costas' : 'Peito'}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-1 block text-xs font-bold uppercase tracking-wider text-amber-100/70">Cor da estampa</legend>
            <div className="flex gap-3">
              {CORES.map((c) => (
                <button
                  key={c.valor}
                  type="button"
                  aria-label={`Cor ${c.nome}`}
                  aria-pressed={cor === c.valor}
                  onClick={() => setCor(c.valor)}
                  className={`h-10 w-10 rounded-full border-2 transition ${cor === c.valor ? 'border-dourado scale-110' : 'border-white/20'}`}
                  style={{ background: c.valor }}
                />
              ))}
            </div>
          </fieldset>

          <p className="rounded-xl border border-dashed border-dourado/25 bg-black/20 p-3 text-xs text-amber-100/60">
            O desenho final (SVG) é salvo junto ao seu pedido para a fábrica conferir o nome exatamente como aparece aqui.
          </p>
        </section>
      </div>
    </div>
  );
}
